import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { outreachApi } from '../../api/outreach'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { emptyState, filterQueue, inboxCaveat, preparedLine, shown } from '../../lib/outreach'
import { Glance } from './Glance'
import { NotListed } from './NotListed'
import { PersonDrawer } from './PersonDrawer'
import { Queue } from './Queue'
import './outreach.css'

// OUTREACH — "somewhere to see which leads I should try contacting, what contact I've had with them
// before, research on the individuals" (REQ-065/066/067). The queue is five at a time, because that is
// how the customer worked his own list: "when I have five minutes, who's the next call."
export function Outreach() {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['outreach', spoke], queryFn: () => outreachApi.list(spoke), staleTime: 10_000 })
  const [reason, setReason] = useState<string>('all')
  const [more, setMore] = useState(0)
  const [openId, setOpenId] = useState<string | null>(null)

  const pick = (kind: string) => { setReason(kind); setMore(0); setOpenId(null) }
  const open = (personId: string) => setOpenId((id) => (id === personId ? null : personId))

  if (q.isPending) return <p className="muted centered">Loading…</p>
  if (q.isError || !q.data) {
    return (
      <section className="page outreach">
        <p className="kicker">Outreach</p>
        <h1>Who to contact today</h1>
        <div className="refusal">
          <strong>Nothing to read yet</strong>
          <p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error?.message ?? 'Nothing here.'}</p>
        </div>
      </section>
    )
  }

  const view = q.data
  const filtered = filterQueue(view.items, reason)
  const visible = shown(filtered.length, view.batch, more)
  const empty = emptyState(view, reason)
  const caveat = inboxCaveat(view)
  const prepared = preparedLine(view)

  return (
    <section className="page outreach">
      <header className="deals-head">
        <div>
          <p className="kicker">{view.scope?.name ? `${view.scope.name} · ` : ''}Outreach</p>
          <h1>Who to contact today</h1>
          <p className="lede">
            {view.items.length === 0
              ? 'Everyone you correspond with, in the order a partner would work them — when there is somebody to work.'
              : `${view.items.length} ${view.items.length === 1 ? 'person is' : 'people are'} up, strongest reason first. Each row says what it is standing on; ${view.batch} at a time, because that is how a morning goes.`}
          </p>
        </div>
        <div className="actions">
          <Link className="button" to="/contacts">Everyone you know</Link>
          <Link className="button" to="/leads">Leads</Link>
        </div>
      </header>

      {caveat && <p className="outreach-caveat" role="note">{caveat}</p>}
      {prepared && <p className="outreach-prepared" role="note">{prepared}</p>}

      <Glance view={view} reason={reason} onPick={pick} />

      {empty ? (
        <div className="card outreach-empty">
          <strong>{empty.title}</strong>
          <p className="muted">{empty.detail}</p>
          {!view.mailboxConnected && <p><Link className="button primary" to="/mailbox">Connect your mailbox</Link></p>}
        </div>
      ) : (
        <div className={`outreach-body ${openId ? 'with-drawer' : ''}`}>
          <div className="outreach-list">
            <Queue items={filtered.slice(0, visible)} openId={openId} onOpen={open} />
            {visible < filtered.length && (
              <p className="outreach-more">
                <button className="button" onClick={() => setMore((n) => n + 1)}>
                  Show {Math.min(view.batch, filtered.length - visible)} more
                </button>
                <span className="muted small">{filtered.length - visible} still waiting</span>
              </p>
            )}
            {visible >= filtered.length && filtered.length > view.batch && (
              <p className="muted small outreach-more">That is everyone on the list today.</p>
            )}
          </div>
          {openId && <PersonDrawer personId={openId} onClose={() => setOpenId(null)} />}
        </div>
      )}

      <NotListed excluded={view.excluded} />
    </section>
  )
}
